<template>
  <div class="sidebar" :class="{ collapsed: collapsed }">
    <!-- Logo区域 -->
    <div class="logo-container">
      <el-icon class="logo-icon" size="32">
        <Shop />
      </el-icon>
      <span v-show="!collapsed" class="logo-text">in·爱Cake</span>
    </div>
    
    <!-- 菜单区域 -->
    <el-scrollbar class="menu-scrollbar">
      <el-menu
        :default-active="activeMenu"
        :collapse="collapsed"
        :unique-opened="true"
        :router="true"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <template v-for="item in menuData" :key="item.path">
          <!-- 单级菜单 -->
          <el-menu-item 
            v-if="!item.children" 
            :index="item.path"
            @click="handleMenuClick(item)"
          >
            <el-icon><component :is="getIconComponent(item.meta.icon)" /></el-icon>
            <template #title>{{ item.meta.title }}</template>
          </el-menu-item>
          
          <!-- 多级菜单 -->
          <el-sub-menu v-else :index="item.path">
            <template #title>
              <el-icon><component :is="getIconComponent(item.meta.icon)" /></el-icon>
              <span>{{ item.meta.title }}</span>
            </template>
            
            <el-menu-item
              v-for="child in item.children"
              :key="child.path"
              :index="child.path"
              @click="handleMenuClick(child)"
            >
              <el-icon><component :is="getIconComponent(child.meta.icon)" /></el-icon>
              <template #title>{{ child.meta.title }}</template>
            </el-menu-item>
          </el-sub-menu>
        </template>
      </el-menu>
    </el-scrollbar>
    
    <!-- 底部版权及备案信息区域 -->
    <div class="footer-container" :class="{ collapsed: collapsed }">
      <div v-show="!collapsed" class="footer-content">
        <div class="copyright-info">
          <p class="company-name">in·爱Cake</p>
          <p class="version-info">版本 v1.0.0</p>
          <p class="copyright-text">© 2025 All Rights Reserved</p>
          <p class="icp-info">
            <a href="#" target="_blank" class="icp-link">
              苏ICP备2025210785号-2
            </a>
          </p>
          <p class="psb-info">
            <img src="/beian.png" alt="备案图标" class="psb-icon" />
            <a href="https://beian.mps.gov.cn/#/query/webSearch?code=32050502012487" rel="noreferrer" target="_blank" class="psb-link">
              苏公网安备32050502012487号
            </a>
          </p>
        </div>
      </div>
      <div v-show="collapsed" class="footer-collapsed">
        <el-tooltip content="系统信息" placement="right">
          <el-icon class="info-icon" size="16">
            <InfoFilled />
          </el-icon>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Odometer,
  DataLine,
  Shop,
  Box,
  Goods,
  Setting,
  Key,
  InfoFilled,
  User,
  UserFilled,
  Lock,
  House,
  List,
  Document,
  ShoppingCart,
  OfficeBuilding,
  Edit,
  Warning,
  Folder,
  DataAnalysis,
  ChatLineRound,
  Connection,
  Cpu,
  Download
} from '@element-plus/icons-vue'

interface MenuItem {
  path: string
  meta: {
    title: string
    icon: string
  }
  children?: MenuItem[]
}

interface Props {
  collapsed: boolean
  menuData: MenuItem[]
}

interface Emits {
  (e: 'menu-select', item: MenuItem): void
  (e: 'update:collapsed', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const route = useRoute()
const router = useRouter()

const activeMenu = computed(() => route.path)

// 图标映射
const iconMap = {
  Dashboard: Odometer,
  DataBoard: DataLine,
  DataAnalysis: DataAnalysis,
  Shop,
  Box,
  House,
  Goods,
  Setting,
  Key,
  InfoFilled,
  User,
  UserFilled,
  Lock,
  List,
  Document,
  ShoppingCart,
  OfficeBuilding,
  Edit,
  Warning,
  Folder,
  ChatLineRound,
  Connection,
  Cpu,
  Download
}

const getIconComponent = (iconName: string) => {
  return iconMap[iconName as keyof typeof iconMap] || Odometer
}

const handleMenuClick = (item: MenuItem) => {
  console.log('Sidebar菜单点击:', item)
  emit('menu-select', item)
  // 确保路由跳转
  if (item.path) {
    router.push(item.path)
  }
}
</script>

<style lang="scss" scoped>
.sidebar {
  width: 240px;
  height: 100vh;
  background-color: #304156;
  transition: width 0.3s ease;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  
  &.collapsed {
    width: 64px;
    
    .logo-text {
      display: none;
    }
  }
  
  .logo-container {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    border-bottom: 1px solid #3a4a5c;
    
          .logo-icon {
        color: #409EFF;
        margin-right: 8px;
      }
    
    .logo-text {
      color: white;
      font-size: 18px;
      font-weight: bold;
    }
  }
  
  .menu-scrollbar {
    height: calc(100vh - 156px); // 60px logo + 96px footer
    
    :deep(.el-menu) {
      border-right: none;
    }
  }
  
  &.collapsed .menu-scrollbar {
    height: calc(100vh - 120px); // 60px logo + 60px footer (collapsed)
  }
}

  .footer-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 96px;
    background-color: #263445;
    border-top: 1px solid #3a4a5c;
    transition: all 0.3s ease;
    z-index: 1001; /* 确保不会溢出到主内容区域 */
    overflow: hidden; /* 防止内容溢出 */
    
    &.collapsed {
      height: 60px;
    }
  
  .footer-content {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    
    .copyright-info {
      text-align: center;
      line-height: 1.2;
      
      p {
        margin: 0;
        padding: 1px 0;
      }
      
      .company-name {
        font-size: 12px;
        font-weight: bold;
        color: #409EFF;
        margin-bottom: 2px;
      }
      
      .version-info {
        font-size: 10px;
        color: #909399;
        margin-bottom: 2px;
      }
      
      .copyright-text {
        font-size: 11px;
        color: #909399;
        margin-bottom: 3px;
      }
      
      .icp-info {
        font-size: 11px;
        color: #909399;
        margin-bottom: 3px;
        
        .icp-link {
          color: #67C23A;
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
            color: #85CE61;
          }
        }
      }
      
      .psb-info {
        font-size: 11px;
        color: #909399;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        margin-bottom: 2px;
        
        .psb-icon {
          width: 14px;
          height: 14px;
          vertical-align: middle;
        }
        
        .psb-link {
          color: #67C23A;
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
            color: #85CE61;
          }
        }
      }
    }
  }
  
  .footer-collapsed {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .info-icon {
      color: #409EFF;
      cursor: pointer;
      
      &:hover {
        color: #66B1FF;
      }
    }
  }
}
</style> 